import { useEffect, useState } from 'react';
import {
  Typography,
  Divider,
  TextField,
  Stack,
  Grid,
  Box,
  Checkbox,
} from '@mui/material';
import { FormikProps } from 'formik';
import type { InvoiceCreateInput, TaxCode } from '@repo/api-contract';
import { useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import { gridItemProps, textFieldProps } from '../../constants/form';
import {
  getFormikField,
  onNumberFieldBlur,
  onNumberFieldFocus,
} from '../../../../util/fornik-helpers';

export default function SaleMarginAndCommission({
  formik,
  viewMode,
}: {
  formik: FormikProps<InvoiceCreateInput>;
  viewMode: boolean;
}): JSX.Element {
  const { t: tAddI } = useTranslation([], {
    keyPrefix: 'team.Modals.Invoices.createAndEdit.Add Invoice',
  });

  const { formData } = useSelector(
    ({ invoiceModalSlice }) => invoiceModalSlice,
  );

  const [sale, setSale] = useState<number>(0);
  const [cost, setCost] = useState<number>(0);
  const [calculatedCommission, setCalculatedCommission] = useState<number>(0);

  useEffect(() => {
    const totals = formData.items.reduce(
      (acc, item) => {
        const qty = item.quantityOrdered ?? 0;

        acc.sale += item.customerPrice * qty;
        acc.cost += item.vendorCost * qty;

        if (item.commissionable && item.commissionRate) {
          acc.commission += item.customerPrice * qty * item.commissionRate;
        }
        return acc;
      },
      { commission: 0, sale: 0, cost: 0 },
    );

    setCalculatedCommission(Math.round(totals.commission * 100) / 100);
    setSale(Math.round(totals.sale * 100) / 100);
    setCost(Math.round(totals.cost * 100) / 100);
  }, [formData.items]);

  return (
    <>
      <Typography color="primary">{tAddI('SALE MARGIN')}</Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Sale')}
              name="sale"
              value={sale.toFixed(2)}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Cost')}
              name="cost"
              value={cost.toFixed(2)}
            />
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Profit Margin')}
              name="profitMargin"
              value={(sale - cost).toFixed(2)}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('% Margin')}
              name="margin"
              value={
                sale === 0
                  ? '0%'
                  : (((sale - cost) / sale) * 100).toFixed(0) + '%'
              }
            />
          </Grid>
        </Stack>
      </Stack>
      <Box
        width={'100%'}
        sx={{
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'flex-end',
          marginTop: 2,
        }}
      >
        <Checkbox
          sx={{ p: 0.1 }}
          checked={getFormikField(formik, 'commissionable').value}
          name="commissionable"
          onChange={formik.handleChange}
        />
        <Typography color="primary">{tAddI('COMMISSION INFO')}</Typography>
      </Box>
      <Divider sx={{ marginBottom: 1 }} />
      <Stack
        gap={2}
        marginY={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Calculated Commission')}
              name="calculatedCommission"
              value={calculatedCommission}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={
                viewMode || !getFormikField(formik, 'commissionable').value
              }
              {...textFieldProps}
              label={tAddI('Commission Adjustment')}
              name="commissionAdjustment"
              type="number"
              onFocus={onNumberFieldFocus(formik, 'commissionAdjustment')}
              onBlur={onNumberFieldBlur(formik, 'commissionAdjustment')}
              error={Boolean(
                getFormikField(formik, 'commissionAdjustment').error,
              )}
              helperText={getFormikField(formik, 'commissionAdjustment').error}
              value={getFormikField(formik, 'commissionAdjustment').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={
                viewMode || !getFormikField(formik, 'commissionable').value
              }
              {...textFieldProps}
              label={tAddI('Adjusted Commision')}
              name="adjustedCommision"
              type="number"
              value={Number(
                calculatedCommission +
                  getFormikField(formik, 'commissionAdjustment').value,
              ).toFixed(2)}
            />
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={
                viewMode || !getFormikField(formik, 'commissionable').value
              }
              {...textFieldProps}
              label={tAddI('Adj. Explained')}
              name="commissionAdjustmentExplanation"
              error={Boolean(
                getFormikField(formik, 'commissionAdjustmentExplanation').error,
              )}
              helperText={
                getFormikField(formik, 'commissionAdjustmentExplanation').error
              }
              value={
                getFormikField(formik, 'commissionAdjustmentExplanation').value
              }
              onChange={formik.handleChange}
            />
          </Grid>
        </Stack>
      </Stack>
    </>
  );
}
